import React, { useState, useEffect, useRef } from 'react';
import { FaTimes, FaIdCard, FaSpinner, FaCheck, FaEdit, FaUser, FaCar, FaExclamationTriangle } from 'react-icons/fa';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import Button from '@/components/common/Button';
import Modal from '@/components/common/Modal';
import { usePlanValidation } from '@/hooks/usePlanValidation';
import useConsultaPatente from '@/hooks/useConsultaPatente';
import VehiculoSelector from '@/components/common/VehiculoSelector';
import { useConfiguracion } from '@/context/ConfiguracionContext';
import PatenteDisplay from '@/components/common/PatenteDisplay';
import RutInput from '@/components/common/RutInput';
import ValidatedInput from '@/components/common/ValidatedInput';
import PaymentModal from '@/components/payments/PaymentModal';
import toastService from '@/utils/toastService';

/**
 * Función para separar nombres chilenos siguiendo el patrón:
 * Nombre(s) Apellido_Paterno Apellido_Materno
 * 
 * Algoritmo basado en patrones comunes en Chile:
 * - Nombres compuestos comunes: Juan Carlos, María José, Ana María, etc.
 * - Apellidos compuestos: De la, Del, Van, Von, etc.
 * - Regla general: Los primeros 1-2 tokens son nombres, el resto apellidos
 */
const separarNombreChileno = (nombreCompleto) => {
  if (!nombreCompleto || typeof nombreCompleto !== 'string') {
    return { nombres: '', apellidos: '' };
  }

  const palabras = nombreCompleto.trim().toUpperCase().split(/\s+/);
  
  if (palabras.length === 0) {
    return { nombres: '', apellidos: '' };
  }
  
  if (palabras.length === 1) {
    return { nombres: palabras[0], apellidos: '' };
  }

  // Nombres compuestos muy comunes en Chile
  const nombresCompuestos = new Set([
    'JUAN CARLOS', 'MARIA JOSE', 'ANA MARIA', 'LUIS ALBERTO', 'CARLOS ALBERTO',
    'JOSE MIGUEL', 'MARIA EUGENIA', 'MARIA TERESA', 'PEDRO PABLO', 'MARIA CRISTINA',
    'MARIA ELENA', 'JUAN PABLO', 'MARIA ANGELICA', 'JOSE ANTONIO', 'MARIA SOLEDAD',
    'MARIA INES', 'ROSA MARIA', 'MARIA VICTORIA', 'JORGE ALBERTO', 'MARIA CARMEN',
    'MARIA FERNANDA', 'FRANCISCO JAVIER', 'MARIA BEATRIZ', 'MARIA MAGDALENA',
    'OSCAR EDUARDO', 'MARIA DOLORES', 'MANUEL ANTONIO', 'MARIA LUISA'
  ]);

  // Prefijos de apellidos compuestos
  const prefijosApellidos = new Set([
    'DE', 'DEL', 'DE LA', 'DE LAS', 'DE LOS', 'VAN', 'VON', 'DA', 'DI', 'DO'
  ]);

  let indiceCorte = 1; // Por defecto, separar después de la primera palabra

  // Verificar si las primeras dos palabras forman un nombre compuesto conocido
  if (palabras.length >= 2) {
    const posibleNombreCompuesto = `${palabras[0]} ${palabras[1]}`;
    if (nombresCompuestos.has(posibleNombreCompuesto)) {
      indiceCorte = 2;
    }
  }

  // Si después del corte inicial hay prefijos de apellidos, ajustar
  if (palabras.length > indiceCorte + 1) {
    const siguientePalabra = palabras[indiceCorte];
    if (prefijosApellidos.has(siguientePalabra)) {
      // Si la siguiente palabra es un prefijo, verificar si es parte de un apellido compuesto
      if (palabras.length > indiceCorte + 2) {
        // Hay suficientes palabras, mantener el corte actual
        // El prefijo quedará como parte de los apellidos
      }
    }
  }

  // Para nombres muy largos (5+ palabras), ser más conservador
  if (palabras.length >= 5) {
    // Si tenemos 5+ palabras, asumir máximo 2 nombres
    indiceCorte = Math.min(indiceCorte, 2);
  }

  // Casos especiales para 3 palabras
  if (palabras.length === 3) {
    // Si la segunda palabra es muy corta (1 letra), probablemente sea inicial
    if (palabras[1].length === 1) {
      indiceCorte = 2; // Incluir la inicial como parte del nombre
    }
    // Si no hay nombre compuesto conocido, usar solo la primera palabra como nombre
    else if (indiceCorte === 1) {
      // Mantener indiceCorte = 1 para patrón: Nombre Apellido1 Apellido2
    }
  }

  const nombres = palabras.slice(0, indiceCorte).join(' ');
  const apellidos = palabras.slice(indiceCorte).join(' ');

  return {
    nombres: nombres || '',
    apellidos: apellidos || ''
  };
};

// Componente interno que no incluye el PaymentModal
const ModalCreacionRapida = ({ isOpen, onClose, onClienteVehiculoCreado }) => {
  const { featureFlags } = useConfiguracion();
  const [patente, setPatente] = useState('');
  const [datosEncontrados, setDatosEncontrados] = useState(null);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [clienteEditado, setClienteEditado] = useState({});
  const [vehiculoEditado, setVehiculoEditado] = useState({
    marca_local: '',
    modelo_local: '',
    marca_global_id: null,
    modelo_global_id: null,
    año: '',
    color: '',
    patente: ''
  });
  const [guardando, setGuardando] = useState(false);
  const { consultarPatente, crearClienteVehiculo, loading, error } = useConsultaPatente();
  const [showQuotaAlert, setShowQuotaAlert] = useState(false);
  
  // Estados para errores de validación
  const [validationErrors, setValidationErrors] = useState({});
  const [showValidationErrors, setShowValidationErrors] = useState(false);

  // Hook para validación de cuotas
  const {
    validateQuota,
    getResourceUsage,
    showPaymentModal,
    targetPlanId,
    closePaymentModal,
    openPaymentModal
  } = usePlanValidation();
  const vehiculoSelectorRef = useRef(null);

  // Verificar si hay recursos al límite
  const checkQuotas = () => {
    const clientesUsage = getResourceUsage('clientes');
    const vehiculosUsage = getResourceUsage('vehiculos');
    
    const clientesAtLimit = clientesUsage?.isAtLimit || false;
    const vehiculosAtLimit = vehiculosUsage?.isAtLimit || false;
    
    // Mostrar alerta si al menos un recurso está al límite
    const shouldShowAlert = clientesAtLimit || vehiculosAtLimit;
    setShowQuotaAlert(shouldShowAlert);
    
    // Devolver false si algún recurso está al límite
    return !shouldShowAlert;
  };


  // Efecto para manejar el estado inicial basado en ENABLE_PATENT_LOOKUP
  useEffect(() => {
    if (isOpen) {
      if (featureFlags?.enablePatentLookup === false) {
        // Si el lookup de patente está desactivado, mostramos directamente el formulario
        setDatosEncontrados({ found: false });
        setClienteEditado({ nombre: '', apellido: '', rut: '', telefono: '', email: '' });
        setVehiculoEditado({ 
          marca_local: '',
          modelo_local: '',
          marca_global_id: null,
          modelo_global_id: null,
          año: '',
          color: '',
          patente: ''
        });
        setModoEdicion(true);
      } else {
        // Si el lookup está activado, reseteamos al estado inicial
        resetModal();
      }
    }
  }, [isOpen, featureFlags?.enablePatentLookup]);

  const handleVehiculoChange = (updates) => {
    setVehiculoEditado(prev => ({
      ...prev,
      ...updates
    }));
    
    // Limpiar errores relacionados cuando el usuario empiece a escribir
    if (showValidationErrors) {
      const newErrors = { ...validationErrors };
      if (updates.patente !== undefined) delete newErrors.patente;
      if (updates.año !== undefined) delete newErrors.año;
      setValidationErrors(newErrors);
    }
  };
  
  // Función para limpiar errores de campos específicos
  const clearFieldError = (fieldName) => {
    if (showValidationErrors && validationErrors[fieldName]) {
      const newErrors = { ...validationErrors };
      delete newErrors[fieldName];
      setValidationErrors(newErrors);
      
      // Si no quedan errores, ocultar el estado de validación
      if (Object.keys(newErrors).length === 0) {
        setShowValidationErrors(false);
      }
    }
  };

  // Validar formato de patente chilena
  const validarPatente = (patente) => {
    const regex = /^[A-Z]{2}[0-9]{4}$|^[A-Z]{4}[0-9]{2}$/;
    return regex.test(patente.toUpperCase());
  };

  const handleConsultaPatente = async () => {
    if (!patente || !validarPatente(patente)) {
      alert('Formato de patente inválido. Usa formato ABCD12 o AB1234');
      return;
    }

    // Verificar si la patente ya existe en la base de datos
    try {
      console.log('🔍 Verificando si la patente ya existe:', patente.toUpperCase());
      // Esta verificación se puede hacer llamando al endpoint de vehículos
      // Por ahora, continuamos con la búsqueda y manejamos el error al final
    } catch (error) {
      console.warn('⚠️ No se pudo verificar la patente existente:', error);
    }

    const resultado = await consultarPatente(patente.toUpperCase(), 'boostr');
    
    if (resultado.found) {
      console.log('🚗 [ModalCreacionRapida] Datos recibidos de la API:', resultado);
      console.log('🚗 [ModalCreacionRapida] Datos del vehículo:', resultado.vehiculo);
      console.log('🚗 [ModalCreacionRapida] Datos del cliente:', resultado.cliente);
      
      setDatosEncontrados(resultado);
      
      // Mapear correctamente los datos del cliente
      const clienteData = resultado.cliente || {};
      console.log('🔍 [ModalCreacionRapida] Datos del cliente recibidos:', clienteData);
      
      let nombre = clienteData.nombre || '';
      let apellido = clienteData.apellido || '';
      
      // Si tenemos nombre_completo y no está vacío, separar en nombre y apellido
      if (clienteData.nombre_completo && clienteData.nombre_completo.trim() && !nombre && !apellido) {
        const nombreCompleto = clienteData.nombre_completo.trim();
        const resultado = separarNombreChileno(nombreCompleto);
        
        console.log('🔍 [ModalCreacionRapida] Separando nombre completo:', nombreCompleto, 'resultado:', resultado);
        
        nombre = resultado.nombres;
        apellido = resultado.apellidos;
      }
      
      const clienteEditadoData = {
        nombre: nombre,
        apellido: apellido,
        rut: clienteData.rut || '',
        telefono: clienteData.telefono || '',
        email: clienteData.email || ''
      };
      
      console.log('🔍 [ModalCreacionRapida] Datos del cliente que se van a establecer:', clienteEditadoData);
      setClienteEditado(clienteEditadoData);
      
      const vehiculoData = {
        ...resultado.vehiculo,
        patente: patente.toUpperCase(),
        // Usar los datos ya mapeados si están disponibles, sino usar los originales
        marca_local: resultado.vehiculo.marca_local || resultado.vehiculo.marca || '',
        modelo_local: resultado.vehiculo.modelo_local || resultado.vehiculo.modelo || '',
        marca_global_id: resultado.vehiculo.marca_global_id || null,
        modelo_global_id: resultado.vehiculo.modelo_global_id || null,
        año: resultado.vehiculo.año || '',
        color: resultado.vehiculo.color || ''
      };
      
      console.log('🚗 [ModalCreacionRapida] Datos del vehículo que se van a establecer:', vehiculoData);
      console.log('🚗 [ModalCreacionRapida] IDs específicos - marca_global_id:', vehiculoData.marca_global_id, 'modelo_global_id:', vehiculoData.modelo_global_id);
      setVehiculoEditado(vehiculoData);
      
      // Siempre activar modo edición cuando se encuentran datos
      setModoEdicion(true);
      
      // Mostrar toast de éxito
      toastService.success(`¡Datos encontrados para patente ${patente}`);
    } else {
      // Si no se encuentran datos, permitir entrada manual
      setDatosEncontrados({ found: false });
      setClienteEditado({ nombre: '', apellido: '', rut: '', telefono: '', email: '' });
      setVehiculoEditado({ 
        marca_local: '',
        modelo_local: '',
        marca_global_id: null,
        modelo_global_id: null,
        año: '', 
        patente: patente.toUpperCase(),
        color: ''
      });
      setModoEdicion(true);
    }
  };

  const validateAllFields = () => {
    console.log('🔍 [ModalCreacionRapida] Validando campos...');
    console.log('🔍 Cliente estado:', clienteEditado);
    console.log('🔍 Vehículo estado:', vehiculoEditado);
    
    let hasErrors = false;
    const errors = {};
    
    // Obtener valores actuales del DOM para asegurar que tengamos los datos más recientes
    const formElements = document.querySelectorAll('#modal-creacion-rapida input, #modal-creacion-rapida select');
    const currentValues = {};
    
    formElements.forEach(element => {
      if (element.name) {
        currentValues[element.name] = element.value;
      }
    });
    
    console.log('🔍 Valores actuales del DOM:', currentValues);
    
    // Validar campos del VehiculoSelector
    if (vehiculoSelectorRef.current && !vehiculoSelectorRef.current.validateFields()) {
      console.log('❌ Error en VehiculoSelector');
      errors.vehiculoSelector = 'Debe seleccionar marca y modelo';
      hasErrors = true;
    }
    
    // Validar nombre (usar DOM si está disponible, sino estado)
    const nombreValue = currentValues.nombre || clienteEditado.nombre || '';
    if (!nombreValue || nombreValue.trim() === '') {
      console.log('❌ Error: Nombre del cliente requerido. Valor:', nombreValue);
      errors.nombre = 'El nombre es requerido';
      hasErrors = true;
    }
    
    // Validar RUT (usar DOM si está disponible, sino estado)  
    const rutValue = currentValues.rut || clienteEditado.rut || '';
    if (!rutValue || rutValue.trim() === '') {
      console.log('❌ Error: RUT del cliente requerido. Valor:', rutValue);
      errors.rut = 'El RUT es requerido';
      hasErrors = true;
    }
    
    // Validar patente
    const patenteValue = currentValues.patente || vehiculoEditado.patente || '';
    if (!patenteValue || patenteValue.trim() === '') {
      console.log('❌ Error: Patente del vehículo requerida. Valor:', patenteValue);
      errors.patente = 'La patente es requerida';
      hasErrors = true;
    }
    
    // Validar año
    const añoValue = currentValues.año || vehiculoEditado.año || '';
    if (!añoValue || añoValue.toString().trim() === '') {
      console.log('❌ Error: Año del vehículo requerido. Valor:', añoValue);
      errors.año = 'El año es requerido';
      hasErrors = true;
    }
    
    console.log('🔍 Validación completada. Errores:', hasErrors);
    console.log('🔍 Errores de validación:', errors);
    
    // Actualizar el estado de errores
    setValidationErrors(errors);
    setShowValidationErrors(hasErrors);
    
    return !hasErrors;
  };

  const handleGuardarClienteVehiculo = async (e) => {
    console.log('🚀 [ModalCreacionRapida] handleGuardarClienteVehiculo iniciado');
    
    // Prevenir propagación del evento para evitar conflictos con otros formularios
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // Validar cuota de vehículos antes de crear
    const canCreateVehiculo = validateQuota('vehiculos', {
      showToast: false,
      showModal: true
    });

    const canCreateCliente = validateQuota('clientes', {
      showToast: false,
      showModal: true
    });
    
    // Actualizar la UI con el estado de las cuotas
    checkQuotas();
    
    if (!canCreateVehiculo || !canCreateCliente) {
      console.log('🚫 No se puede crear cliente o vehículo - límite alcanzado');
      return; // Bloquear el envío del formulario
    }
    
    // Validar todos los campos inmediatamente
    if (!validateAllFields()) {
      console.log('🚫 Validación fallida, no se enviará el formulario');
      return;
    }
    
    setGuardando(true);
    
    try {
      // Prepare vehicle data in the expected format for the API
      const vehiculoData = {
        ...vehiculoEditado,
        // Map the fields to the expected API format
        marca: vehiculoEditado.marca_local,
        modelo: vehiculoEditado.modelo_local,
        patente: vehiculoEditado.patente,
        año: vehiculoEditado.año,
        color: vehiculoEditado.color
      };

      console.log('Guardando cliente y vehículo:', { 
        cliente: clienteEditado, 
        vehiculo: vehiculoData
      });

      // Usar el hook que maneja toda la lógica a través del servicio
      const resultado = await crearClienteVehiculo(clienteEditado, vehiculoData);
      
      if (resultado.success) {
        console.log('Cliente y vehículo creados exitosamente:', resultado);
        
        // Llamar al callback con los datos creados
        onClienteVehiculoCreado(resultado.cliente, resultado.vehiculo);
        
        // Cerrar el modal después de la creación exitosa
        handleClose();
        
      } else {
        // Mejorar el manejo de errores específicos
        const errorMsg = resultado.error || 'Error al crear cliente y vehículo';
        
        if (errorMsg.includes('duplicate key') || errorMsg.includes('patente') || errorMsg.includes('vehiculos_pkey')) {
          alert(`⚠️ Esta patente (${vehiculoEditado.patente}) ya existe en el sistema.\n\nPor favor verifica la patente o busca el vehículo existente.`);
        } else if (errorMsg.includes('RUT')) {
          alert(`⚠️ Ya existe un cliente con este RUT (${clienteEditado.rut}).\n\nPor favor verifica el RUT o busca el cliente existente.`);
        } else {
          alert(errorMsg);
        }
      }
      
    } catch (error) {
      console.error('Error inesperado guardando cliente y vehículo:', error);
      
      // Mejorar manejo de errores inesperados también
      const errorStr = error.toString();
      if (errorStr.includes('duplicate key') || errorStr.includes('patente')) {
        alert(`⚠️ Esta patente (${vehiculoEditado.patente}) ya existe en el sistema.\n\nPor favor verifica la patente o busca el vehículo existente.`);
      } else {
        alert('Error inesperado al guardar los datos. Por favor intenta nuevamente.');
      }
    } finally {
      setGuardando(false);
    }
  };

  const resetModal = () => {
    setPatente('');
    setDatosEncontrados(null);
    setModoEdicion(false);
    setClienteEditado({});
    setVehiculoEditado({
      marca_local: '',
      modelo_local: '',
      marca_global_id: null,
      modelo_global_id: null,
      año: '',
      color: '',
      patente: ''
    });
    // Reset VehiculoSelector validation if ref exists
    if (vehiculoSelectorRef.current) {
      vehiculoSelectorRef.current.resetValidation();
    }
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  const modalTitle = (
    <div className="flex items-center">
      <FaIdCard className="mr-2 text-blue-400" />
      <span>Crear Cliente y Vehículo</span>
    </div>
  );

  const modalSize = !datosEncontrados && featureFlags?.enablePatentLookup !== false 
    ? 'md' // Ancho estrecho para consulta de patente
    : '6xl'; // Ancho completo para formulario

  const modalFooter = (
    <div className="flex justify-end gap-3">
      <Button
        variant="secondary"
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleClose();
        }}
        className="py-2.5"
      >
        Cancelar
      </Button>
      {datosEncontrados && (
        <Button
          variant="primary"
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleGuardarClienteVehiculo(e);
          }}
          loading={guardando}
          disabled={guardando}
          className="py-2.5 px-6"
        >
          Crear Cliente y Vehículo
        </Button>
      )}
    </div>
  );

  if (!isOpen) return null;

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title={modalTitle}
        size={modalSize}
        footer={modalFooter}
      >
        {/* Content area */}
        <div className="space-y-6">
            {/* Alerta de cuota - Se muestra solo cuando ambos recursos están al límite */}
            {showQuotaAlert && (
              <div className="bg-red-500/10 border border-red-500 rounded-lg p-3 mb-4">
                <div className="flex items-center justify-between gap-3">
                  {/* Icono y mensaje principal */}
                  <div className="flex items-center min-w-0">
                    <FaExclamationTriangle className="h-5 w-5 text-red-500 flex-shrink-0" />
                    <div className="ml-3">
                      <p className="text-sm text-white">
                        Límite de clientes y vehículos alcanzado
                      </p>
                      <p className="text-xs text-gray-300 mt-0.5">
                        Has alcanzado el límite de clientes y vehículos permitidos en tu plan actual.
                      </p>
                    </div>
                  </div>
                  
                  {/* Botón de acción */}
                  <button
                    onClick={() => openPaymentModal()}
                    className="text-sm font-medium px-4 py-2 rounded-lg transition-colors bg-red-500 hover:bg-red-600 text-white border border-red-600 whitespace-nowrap flex items-center"
                  >
                    <span>Mejorar plan</span>
                    <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 ml-1.5" />
                  </button>
                </div>
              </div>
            )}
            {/* Paso 1: Consulta por patente - Solo se muestra si ENABLE_PATENT_LOOKUP es true */}
            {!datosEncontrados && featureFlags?.enablePatentLookup !== false && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Patente del vehículo
                  </label>
                  <div className="flex gap-3">
                    <input
                      type="text"
                      value={patente}
                      onChange={(e) => {
                        const value = e.target.value.toUpperCase();
                        if (value.length <= 7) {
                          setPatente(value);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && patente && !loading) {
                          e.preventDefault();
                          handleConsultaPatente();
                        }
                      }}
                      placeholder="Ej: ABCD12 o AB1234"
                      className="flex-1 p-3 rounded-lg bg-gray-700 border border-gray-600 text-white placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                      maxLength={7}
                    />
                    <Button
                      onClick={handleConsultaPatente}
                      disabled={loading || !patente}
                      className="px-6"
                    >
                      {loading ? (
                        <FaSpinner className="animate-spin" />
                      ) : (
                        'Buscar'
                      )}
                    </Button>
                  </div>
                  {error && (
                    <p className="text-red-400 text-sm mt-2">{error}</p>
                  )}
                  <p className="text-gray-400 text-sm mt-2">
                    Buscaremos automáticamente los datos del propietario y vehículo
                  </p>
                </div>
              </div>
            )}

            {/* Paso 2: Mostrar datos encontrados o formulario manual */}
            {(datosEncontrados || featureFlags?.enablePatentLookup === false) && (
              <div className="space-y-6">
                {/* Datos encontrados automáticamente */}
                {datosEncontrados?.found && !modoEdicion && (
                  <div className="bg-green-900/20 border border-green-700 rounded-lg p-4">
                    <div className="flex items-center mb-3">
                      <FaCheck className="text-green-400 mr-2" />
                      <span className="text-green-400 font-medium">Datos encontrados automáticamente</span>
                      <button
                        onClick={() => setModoEdicion(true)}
                        className="ml-auto text-blue-400 hover:text-blue-300 text-sm flex items-center"
                      >
                        <FaEdit className="mr-1" />
                        Editar
                      </button>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <h4 className="text-white font-medium mb-2 flex items-center">
                          <FaUser className="mr-2 text-blue-400" />
                          Cliente
                        </h4>
                        <p className="text-gray-300">Nombre: {clienteEditado.nombre}</p>
                        <p className="text-gray-300">RUT: {clienteEditado.rut}</p>
                      </div>
                      <div>
                        <h4 className="text-white font-medium mb-2 flex items-center">
                          <FaCar className="mr-2 text-blue-400" />
                          Vehículo
                        </h4>
                        <p className="text-gray-300">Marca: {vehiculoEditado.marca}</p>
                        <p className="text-gray-300">Modelo: {vehiculoEditado.modelo}</p>
                        <p className="text-gray-300">Año: {vehiculoEditado.año}</p>
                        <p className="text-gray-300">Patente: {vehiculoEditado.patente}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Formulario de edición / entrada manual */}
                {(modoEdicion || !datosEncontrados?.found) && (
                  <div className="space-y-6">
                    {!datosEncontrados?.found && featureFlags?.enablePatentLookup !== false && (
                      <div className="bg-yellow-900/20 border border-yellow-700 rounded-lg p-4">
                        <p className="text-yellow-400 text-sm">
                          No se encontraron datos automáticamente. Completa la información manualmente.
                        </p>
                      </div>
                    )}

                    <div 
                      className="md:grid md:grid-cols-2 md:gap-6"
                      onSubmit={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        return false;
                      }}
                      onInvalid={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        return false;
                      }}
                    >
                      {/* Columna izquierda: Información del Cliente */}
                      <div 
                        className="bg-gray-800 p-4 rounded-lg mb-6 md:mb-0 border border-gray-600"
                        onSubmit={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          return false;
                        }}
                        onInvalid={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          return false;
                        }}
                      >
                        <div className="flex items-center mb-4 pb-2 border-b border-gray-600">
                          <FaUser className="text-blue-400 mr-2" />
                          <h4 className="text-white font-medium">Información Básica</h4>
                        </div>
                        
                        {/* Nombre */}
                        <div className="mb-4">
                          <label className="flex items-center text-sm font-medium mb-2">
                            <span className="text-gray-400 mr-2">Nombre</span>
                            <span className="text-red-500">*</span>
                          </label>
                          <ValidatedInput
                            value={clienteEditado.nombre || ''}
                            onChange={(e) => {
                              console.log('📝 Nombre cambiado:', e.target.value);
                              setClienteEditado(prev => ({ ...prev, nombre: e.target.value }));
                              clearFieldError('nombre');
                            }}
                            placeholder="Ej: Juan Carlos"
                            required
                            name="nombre"
                            requiredMessage="El nombre es requerido"
                            forceShowValidation={showValidationErrors && !!validationErrors.nombre}
                          />
                        </div>
                        
                        {/* Apellido */}
                        <div className="mb-4">
                          <label className="flex items-center text-sm font-medium mb-2">
                            <span className="text-gray-400 mr-2">Apellido</span>
                          </label>
                          <ValidatedInput
                            value={clienteEditado.apellido || ''}
                            onChange={(e) => setClienteEditado(prev => ({ ...prev, apellido: e.target.value }))}
                            placeholder="Ej: González Pérez"
                            name="apellido"
                          />
                        </div>
                        
                        {/* RUT y Teléfono en la misma fila */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="flex items-center text-sm font-medium mb-2">
                              <span className="text-gray-400 mr-2">RUT</span>
                              <span className="text-red-500">*</span>
                            </label>
                            <RutInput
                              value={clienteEditado.rut || ''}
                              onChange={(e) => {
                                console.log('📝 RUT cambiado:', e.target.value);
                                setClienteEditado(prev => ({ ...prev, rut: e.target.value }));
                                clearFieldError('rut');
                              }}
                              placeholder="Ej: 12.345.678-9"
                              required
                              name="rut"
                              showValidation={true}
                              forceShowValidation={showValidationErrors && !!validationErrors.rut}
                            />
                          </div>
                          <div>
                            <label className="flex items-center text-sm font-medium mb-2">
                              <span className="text-gray-400 mr-2">Teléfono</span>
                            </label>
                            <ValidatedInput
                              type="tel"
                              value={clienteEditado.telefono || ''}
                              onChange={(e) => setClienteEditado(prev => ({ ...prev, telefono: e.target.value }))}
                              placeholder="Ej: +56 9 1234 5678"
                              name="telefono"
                            />
                          </div>
                        </div>
                      </div>
                      
                      {/* Columna derecha: Detalles del Vehículo */}
                      <div className="bg-gray-800 p-4 rounded-lg border border-gray-600">
                        <div className="flex items-center mb-4 pb-2 border-b border-gray-600">
                          <FaCar className="text-green-400 mr-2" />
                          <h4 className="text-white font-medium">Detalles del Vehículo</h4>
                        </div>
                        
                        {/* Patente */}
                        <div className="mb-4">
                          <label className="flex items-center text-sm font-medium mb-2">
                            <span className="text-gray-400 mr-2">Patente</span>
                            <span className="text-red-500">*</span>
                          </label>
                          <div className="flex flex-col md:flex-row md:items-center gap-3">
                            <div className="flex-1">
                              <ValidatedInput
                                type="text"
                                name="patente"
                                placeholder="Ej: ABC123"
                                value={vehiculoEditado.patente || ''}
                                onChange={(e) => {
                                  const value = e.target.value.toUpperCase();
                                  if (value.length <= 7) {
                                    handleVehiculoChange({ patente: value });
                                  }
                                }}
                                maxLength={7}
                                required
                                requiredMessage="La patente es requerida"
                                forceShowValidation={showValidationErrors && !!validationErrors.patente}
                              />
                            </div>
                            {vehiculoEditado.patente && vehiculoEditado.patente.length >= 3 && (
                              <div className="flex items-center justify-center px-3 py-2 bg-gray-900 rounded-lg border border-gray-600">
                                <PatenteDisplay patente={vehiculoEditado.patente} size="sm" />
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Selector de Marca y Modelo */}
                        <div className="mb-4">
                          {/* Log de props que se pasan al VehiculoSelector */}
                          {console.log('🚗 [ModalCreacionRapida] Props para VehiculoSelector:', {
                            marcaId: vehiculoEditado.marca_global_id,
                            modeloId: vehiculoEditado.modelo_global_id,
                            marcaValue: vehiculoEditado.marca_local,
                            modeloValue: vehiculoEditado.modelo_local,
                            'typeof marcaId': typeof vehiculoEditado.marca_global_id,
                            'typeof modeloId': typeof vehiculoEditado.modelo_global_id
                          })}
                          <VehiculoSelector
                            ref={vehiculoSelectorRef}
                            marcaId={vehiculoEditado.marca_global_id}
                            modeloId={vehiculoEditado.modelo_global_id}
                            marcaValue={vehiculoEditado.marca_local}
                            modeloValue={vehiculoEditado.modelo_local}
                            onMarcaChange={(e) => {
                              const value = e?.target?.value;
                              const marca = e?.target?.marca || null;
                              const marcaId = marca?.id || null;
                              console.log('🚗 [ModalCreacionRapida] onMarcaChange - value:', value, 'marca:', marca, 'marcaId:', marcaId);
                              
                              // Solo resetear modelo si es una selección manual del usuario (marca diferente)
                              const isUserSelection = marcaId !== vehiculoEditado.marca_global_id;
                              
                              handleVehiculoChange({
                                marca_global_id: marcaId,
                                marca_local: value || '',
                                // Solo resetear modelo en selecciones manuales del usuario
                                ...(isUserSelection && {
                                  modelo_global_id: null,
                                  modelo_local: ''
                                })
                              });
                            }}
                            onModeloChange={(e) => {
                              const modelo = e?.target?.modelo || null;
                              const modelName = e?.target?.value || '';
                              const modeloId = modelo?.id || null;
                              console.log('🚗 [ModalCreacionRapida] onModeloChange - modelName:', modelName, 'modelo:', modelo, 'modeloId:', modeloId);
                              handleVehiculoChange({
                                modelo_global_id: modeloId,
                                modelo_local: modelName
                              });
                            }}
                            className="w-full"
                            required
                          />
                        </div>
                        
                        {/* Año y Color */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                          <div>
                            <label className="flex items-center text-sm font-medium mb-2">
                              <span className="text-gray-400 mr-2">Año</span>
                              <span className="text-red-500">*</span>
                            </label>
                            <ValidatedInput
                              type="number"
                              name="año"
                              value={vehiculoEditado.año || ''}
                              onChange={(e) => {
                                const value = e.target.value;
                                const numValue = parseInt(value);
                                const currentYear = new Date().getFullYear();
                                
                                // Permitir campo vacío
                                if (value === '') {
                                  handleVehiculoChange({ año: '' });
                                  return;
                                }
                                
                                // Permitir escribir números de hasta 4 dígitos
                                if (value.length <= 4) {
                                  // Si es un número completo de 4 dígitos, validar rango
                                  if (value.length === 4) {
                                    if (numValue >= 1900 && numValue <= currentYear + 1) {
                                      handleVehiculoChange({ año: value });
                                    }
                                  } else {
                                    // Permitir escribir mientras construye el número (1, 19, 190, etc.)
                                    handleVehiculoChange({ año: value });
                                  }
                                }
                              }}
                              min={1900}
                              max={new Date().getFullYear() + 1}
                              placeholder="Ej: 2020"
                              required
                              requiredMessage="El año es requerido"
                              forceShowValidation={showValidationErrors && !!validationErrors.año}
                            />
                          </div>
                          <div>
                            <label className="flex items-center text-sm font-medium mb-2">
                              <span className="text-gray-400 mr-2">Color</span>
                            </label>
                            <input
                              type="text"
                              value={vehiculoEditado.color || ''}
                              onChange={(e) => handleVehiculoChange({ color: e.target.value })}
                              className="w-full p-2 rounded-lg bg-gray-700 border border-gray-600 text-white placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                              placeholder="Ej: Blanco, Negro, Rojo"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
      </Modal>

      {/* Modal de pago para upgrade de plan */}
      <PaymentModal 
        isOpen={showPaymentModal}
        onClose={closePaymentModal}
        targetPlanId={targetPlanId}
      />
    </>
  );
};

// Exportar directamente el componente principal
export default ModalCreacionRapida;